import { compareValues, saveState } from './storage.js';
import { updateSortHeaders, renderPagination } from './render-ui.js';
import { markRenderArtifact } from '../../shared/render-artifact.js';

export function createGridRenderer(deps) {
    const {
        grid,
        body,
        storageKey,
        infoEl,
        paginationEl,
        emptyMessage,
        dateRangeEnabled,
        persistDateRange,
        getItems,
        getFilteredRows,
        getSortedRows,
        setItemVisible,
        materializeItem,
        searchSelector,
    } = deps;

    let renderGeneration = 0;
    let pendingBodyApply = null;

    function cancelPending() {
        if (!pendingBodyApply) return;
        if (pendingBodyApply.type === 'timeout') {
            clearTimeout(pendingBodyApply.id);
        } else {
            cancelAnimationFrame(pendingBodyApply.id);
        }
        pendingBodyApply = null;
    }

    function syncSearchInputs(value) {
        if (!searchSelector) return;
        document.querySelectorAll(searchSelector).forEach(el => {
            if (el === document.activeElement) return;
            el.value = value;
        });
    }

    function syncDateRangeInputs(state, dateFromSelector, dateToSelector) {
        if (!dateRangeEnabled) return;
        document.querySelectorAll(dateFromSelector).forEach(el => { el.value = state.dateFrom; });
        document.querySelectorAll(dateToSelector).forEach(el => { el.value = state.dateTo; });
    }

    function render(state, renderOptions) {
        renderOptions = renderOptions || {};
        cancelPending();
        const deferMs = renderOptions.deferMs || 0;
        if (deferMs > 0) {
            const timeoutId = setTimeout(() => render(state, { syncBody: true }), deferMs);
            pendingBodyApply = { type: 'timeout', id: timeoutId };
            return;
        }
        const gen = ++renderGeneration;

        const filtered = getFilteredRows(state);
        const sorted = getSortedRows(filtered, state);
        const total = sorted.length;
        const totalPages = Math.max(1, Math.ceil(total / state.pageLength));
        const currentPage = Math.min(state.page, totalPages);
        const start = (currentPage - 1) * state.pageLength;
        const pageRows = sorted.slice(start, start + state.pageLength);
        const pageSet = new Set(pageRows);

        if (infoEl) {
            const from = total === 0 ? 0 : start + 1;
            const to = Math.min(start + state.pageLength, total);
            const infoText = total === 0 ? emptyMessage : `Showing ${from} to ${to} of ${total}`;
            if (infoEl.textContent !== infoText) infoEl.textContent = infoText;
        }

        renderPagination(paginationEl, currentPage, totalPages, (pageNum) => {
            state.page = pageNum;
            render(state);
        });
        if (state.sortKey) updateSortHeaders(grid, state.sortKey, state.sortDir);
        const persisted = {
            search: state.searchQuery,
            statusFilter: state.statusFilter,
            recurringStatusFilter: state.recurringStatusFilter,
            typeFilter: state.typeFilter,
            sortKey: state.sortKey,
            sortDir: state.sortDir,
            page: currentPage,
            pageLength: state.pageLength,
        };
        if (dateRangeEnabled && persistDateRange) {
            persisted.dateFrom = state.dateFrom;
            persisted.dateTo = state.dateTo;
        }
        saveState(storageKey, persisted);
        state.page = currentPage;

        function syncEmptyHint(rowTotal) {
            let hint = body.querySelector('[data-grid-empty-hint]');
            if (rowTotal > 0) {
                if (hint) {
                    hint.remove();
                }
                return;
            }
            if (!hint) {
                hint = markRenderArtifact(document.createElement('div'));
                hint.className = 'data-grid-empty-hint';
                hint.setAttribute('data-grid-empty-hint', '');
                hint.setAttribute('role', 'status');
                body.appendChild(hint);
            }
            hint.textContent = emptyMessage;
        }

        function applyBody() {
            pendingBodyApply = null;
            if (gen !== renderGeneration) return;
            getItems().forEach(function (item) {
                setItemVisible(item, pageSet.has(item));
            });
            const frag = document.createDocumentFragment();
            pageRows.forEach(function (item) {
                materializeItem(item);
                frag.appendChild(item);
            });
            body.appendChild(frag);
            syncEmptyHint(total);
            grid.dispatchEvent(new CustomEvent('data-grid-rendered', { bubbles: true }));
        }

        if (renderOptions.syncBody) {
            applyBody();
            return;
        }

        const frameId = requestAnimationFrame(applyBody);
        pendingBodyApply = { type: 'raf', id: frameId };
    }

    return {
        render,
        syncSearchInputs,
        syncDateRangeInputs,
        cancelPending,
    };
}

export function createRowQuery(deps) {
    const {
        getItems,
        getSortValue,
        createSearchMatcher,
        rowMatchesStatus,
        rowMatchesType,
        createCampaignMatcher,
        rowMatchesDateRangeFn,
        dateRangeEnabled,
    } = deps;

    function getEffectiveStatusFilter(state, type, status, recurringStatus) {
        if (type === 'Donation') return '';
        if (type === 'Recurring') return recurringStatus ?? state.recurringStatusFilter;
        return status ?? state.statusFilter;
    }

    function getFilteredRows(state) {
        const rowMatchesSearch = createSearchMatcher(state.searchQuery.trim());
        const effectiveStatus = getEffectiveStatusFilter(state, state.typeFilter, state.statusFilter, state.recurringStatusFilter);
        const rowMatchesCampaign = typeof createCampaignMatcher === 'function' ? createCampaignMatcher() : null;
        return getItems().filter(item => {
            const row = item.querySelector('.data-grid-row');
            if (!row) return false;
            if (!rowMatchesSearch(row) || !rowMatchesStatus(row, effectiveStatus) || !rowMatchesType(row, state.typeFilter)) return false;
            if (rowMatchesCampaign && !rowMatchesCampaign(row)) return false;
            if (dateRangeEnabled && !rowMatchesDateRangeFn(row, state.dateFrom, state.dateTo)) return false;
            return true;
        });
    }

    function readSortAt(row) {
        const sortAt = row?.dataset.sortAt;
        return sortAt === undefined ? NaN : Number(sortAt);
    }

    function getSortedRows(rows, state) {
        if (!state.sortKey) return [...rows];
        const keyed = rows.map((item) => {
            const row = item.querySelector('.data-grid-row');
            return { item, value: getSortValue(row, state.sortKey), sortAt: readSortAt(row) };
        });
        keyed.sort((a, b) => {
            const cmp = compareValues(a.value, b.value, state.sortDir);
            if (cmp !== 0 || Number.isNaN(a.sortAt) || Number.isNaN(b.sortAt)) return cmp;
            return compareValues(a.sortAt, b.sortAt, state.sortDir);
        });
        return keyed.map((entry) => entry.item);
    }

    return { getFilteredRows, getSortedRows };
}
